import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, CheckCircle2, Leaf } from 'lucide-react';
import img1 from '../assets/images/jpg/img1.jpg';

const businessMapUrl = 'https://maps.app.goo.gl/jpcSNMC3kdKK1b99A?g_st=iw';
const businessMapEmbedUrl = 'https://www.google.com/maps?q=Christchurch%2C%20New%20Zealand&output=embed';

export default function Contact() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [formStatus, setFormStatus] = useState({ sending: false, message: '', error: false });

  const variants = {
    container: { hidden: {}, show: { transition: { staggerChildren: 0.12 } } },
    fadeUp: { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } } },
  };

  const cardVariant = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormStatus({ sending: true, message: '', error: false });

    try {
      const response = await fetch('/contact.php', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(event.currentTarget)
      });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || 'Unable to send your message.');
      setFormData({ name: '', email: '', phone: '', message: '' });
      setFormStatus({ sending: false, message: result.message, error: false });
    } catch (error) {
      setFormStatus({ sending: false, message: error.message || 'Unable to send your message. Please try again.', error: true });
    }
  };

  return (
    <div className="bg-white overflow-x-hidden">
      {/* 1. HERO SECTION */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.25 }}
        variants={variants.container}
        className="relative bg-emerald-950 text-white pt-40 pb-24 px-24 overflow-hidden"
      >
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: `url(${img1})` }}></div>
        
        <div className="relative max-w-7xl mx-auto text-center space-y-6">
          <motion.div variants={variants.fadeUp}>
            <span className="inline-flex items-center gap-2 bg-lime-500/10 border border-lime-500/30 text-lime-400 text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest">
              <Leaf size={14} className="fill-current" /> Get In Touch
            </span>
          </motion.div>
          
          <motion.h1 variants={variants.fadeUp} className="text-4xl md:text-6xl font-black text-white tracking-tight leading-[1.1]">
            Contact <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-lime-400 to-emerald-400">
              Landscaping Christchurch
            </span>
          </motion.h1>
          
          <motion.p variants={variants.fadeUp} className="text-base md:text-lg text-emerald-100/80 max-w-xl mx-auto font-normal leading-relaxed">
            Have a question or ready to start your landscaping project? We'd love to hear from you. Reach out today!
          </motion.p>
        </div>
      </motion.section>

      {/* 2. CONTACT FORM & INFO SECTION */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="max-w-7xl mx-auto px-4 py-24 grid grid-cols-1 lg:grid-cols-2 gap-16"
      >
        {/* Left: Contact Form */}
        <motion.div variants={cardVariant} className="space-y-6">
          <div>
            <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight mb-6">
              Send Us a Message
            </h2>
            <p className="text-slate-600 leading-relaxed mb-8">
              Fill out the form below and we'll get back to you as soon as possible. We're here to answer any questions!
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-bold text-emerald-950 mb-2">Full Name *</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:border-lime-500 focus:ring-2 focus:ring-lime-500/20 transition"
                placeholder="Your Name"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-emerald-950 mb-2">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:border-lime-500 focus:ring-2 focus:ring-lime-500/20 transition"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-emerald-950 mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:border-lime-500 focus:ring-2 focus:ring-lime-500/20 transition"
                  placeholder="+64 228 980 029"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-bold text-emerald-950 mb-2">Message *</label>
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows="5"
                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:border-lime-500 focus:ring-2 focus:ring-lime-500/20 transition resize-none"
                placeholder="Tell us about your project..."
              />
            </div>

            <motion.button
              type="submit"
              disabled={formStatus.sending}
              whileTap={{ scale: 0.98 }}
              className="w-full bg-lime-500 hover:bg-lime-600 text-emerald-950 px-8 py-4 rounded-xl font-extrabold text-base transition-all shadow-lg shadow-lime-500/20 flex items-center justify-center gap-2"
            >
              {formStatus.sending ? 'Sending...' : 'Send Message'} <Send size={18} />
            </motion.button>

            {formStatus.message && (
              <p className={`px-4 py-3 rounded-xl font-bold ${formStatus.error ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-800'}`}>
                {formStatus.message}
              </p>
            )}

          </form>
        </motion.div>

        {/* Right: Contact Info */}
        <motion.div variants={cardVariant} className="space-y-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-black text-emerald-950 tracking-tight mb-8">
              Contact Information
            </h2>
          </div>

          {/* Phone */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-all">
            <div className="flex items-start gap-4">
              <div className="bg-lime-100 text-lime-700 p-3 rounded-xl mt-1">
                <Phone size={24} />
              </div>
              <div>
                <h3 className="font-black text-emerald-950 text-lg mb-2">Phone</h3>
                <a href="tel:+64228980029" className="text-slate-600 font-semibold hover:text-lime-600 transition text-lg">
                  +64 228 980 029
                </a>
                <p className="text-sm text-slate-500 mt-1">Mon-Fri: 9am-8pm | Sat: 10am-8pm</p>
              </div>
            </div>
          </div>

          {/* Email */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-all">
            <div className="flex items-start gap-4">
              <div className="bg-lime-100 text-lime-700 p-3 rounded-xl mt-1">
                <Mail size={24} />
              </div>
              <div>
                <h3 className="font-black text-emerald-950 text-lg mb-2">Email</h3>
                <a href="mailto:Info@thelandscapingchristchurch.co.nz" className="text-slate-600 font-semibold hover:text-lime-600 transition text-lg">
                  Info@thelandscapingchristchurch.co.nz
                </a>
                <p className="text-sm text-slate-500 mt-1">We respond within 24 hours</p>
              </div>
            </div>
          </div>

          {/* Address */}
          <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 hover:shadow-lg transition-all">
            <div className="flex items-start gap-4">
              <div className="bg-lime-100 text-lime-700 p-3 rounded-xl mt-1">
                <MapPin size={24} />
              </div>
              <div>
                <h3 className="font-black text-emerald-950 text-lg mb-2">Address</h3>
                <a href={businessMapUrl} target="_blank" rel="noreferrer" className="block text-slate-600 font-semibold mb-2 hover:text-lime-600 transition">
                  Christchurch, New Zealand
                </a>
                <p className="text-sm text-slate-500">Serving Christchurch and surrounding areas</p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.section>

      {/* 3. GOOGLE MAPS */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="max-w-7xl mx-auto px-4 py-24"
      >
        <motion.div variants={cardVariant} className="space-y-6">
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight">
            Our Location
          </h2>
          <div className="w-full h-[500px] rounded-3xl overflow-hidden shadow-xl border-8 border-white">
            <iframe
              src={businessMapEmbedUrl}
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Landscaping Christchurch Location"
            />
          </div>
        </motion.div>
      </motion.section>

      {/* 4. FAQ / WHY CHOOSE US */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="bg-stone-50 py-24 px-4"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16 space-y-3">
            <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight">
              Why Contact Us Today?
            </h2>
            <div className="h-1.5 w-16 bg-lime-500 mx-auto rounded-full"></div>
          </div>

          <motion.div className="grid grid-cols-1 md:grid-cols-2 gap-8" variants={variants.container}>
            {[
              { title: 'Fast Response', desc: 'We reply to all inquiries within 24 hours during business days.' },
              { title: 'Free Consultation', desc: 'Get a complimentary site visit and custom proposal for your project.' },
              { title: 'Expert Team', desc: 'Speak directly with our experienced landscape architects and designers.' },
              { title: 'Transparent Pricing', desc: 'No hidden costs — we provide detailed estimates upfront.' },
              { title: '24/7 Support', desc: 'Emergency landscaping issues? We have a dedicated support line.' },
              { title: 'Flexible Scheduling', desc: 'We work around your schedule to make consultations convenient.' }
            ].map((item, idx) => (
              <motion.div key={idx} variants={cardVariant} className="bg-white rounded-2xl p-8 border border-slate-100 hover:shadow-lg transition-all">
                <div className="flex items-start gap-3 mb-3">
                  <CheckCircle2 className="text-lime-500 flex-shrink-0 mt-1" size={22} />
                  <h3 className="font-black text-emerald-950 text-lg">{item.title}</h3>
                </div>
                <p className="text-slate-600 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* 5. CTA SECTION */}
      <motion.section
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.2 }}
        variants={variants.container}
        className="max-w-7xl mx-auto px-4 py-24 text-center"
      >
        <motion.div variants={cardVariant} className="space-y-6">
          <h2 className="text-3xl md:text-5xl font-black text-emerald-950 tracking-tight">
            Ready to Get Started?
          </h2>
          <p className="text-slate-600 text-lg max-w-2xl mx-auto">
            Don't wait any longer. Contact us today and let's discuss your landscaping dreams!
          </p>
          <motion.div variants={variants.fadeUp} className="flex flex-wrap gap-4 justify-center pt-4">
            <motion.a
              href="tel:+64228980029"
              whileTap={{ scale: 0.98 }}
              className="bg-lime-500 hover:bg-lime-600 text-emerald-950 px-8 py-4 rounded-xl font-extrabold text-base transition-all shadow-lg shadow-lime-500/20 inline-flex items-center gap-2"
            >
              Call Now <Phone size={18} />
            </motion.a>
            <motion.a
              href="mailto:Info@thelandscapingchristchurch.co.nz"
              whileTap={{ scale: 0.98 }}
              className="bg-emerald-900/50 hover:bg-emerald-900 border border-emerald-800 text-white px-8 py-4 rounded-xl font-bold text-base transition-all inline-flex items-center gap-2"
            >
              Email Us
            </motion.a>
          </motion.div>
        </motion.div>
      </motion.section>
    </div>
  );
}
